// pages/Debts.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import AddDebtModal from '../components/AddDebtModal';
import { formatCurrency, formatLocalDate } from '../utils';

const Debts = () => {
  const navigate = useNavigate();
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    credit_card: { key: 'name', direction: 'asc' },
    personal_loan: { key: 'name', direction: 'asc' },
    student_loan: { key: 'name', direction: 'asc' },
    auto_loan: { key: 'name', direction: 'asc' },
    mortgage: { key: 'name', direction: 'asc' },
    other: { key: 'name', direction: 'asc' }
  });
  const [hoveredDebt, setHoveredDebt] = useState(null);

  // Define debt type order (credit cards first as most important)
  const debtTypeOrder = {
    credit_card: 1,
    personal_loan: 2,
    student_loan: 3,
    other: 4,
    auto_loan: 5,
    mortgage: 6
  };

  useEffect(() => {
    fetchDebts();
  }, []);

  const fetchDebts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_PATH}/debts`);
      setDebts(response.data || []);
    } catch (error) {
      console.error('Error fetching debts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDebt = () => {
    setEditingDebt(null);
    setShowAddModal(true);
  };

  const handleEditDebt = (debt) => {
    setEditingDebt(debt);
    setShowAddModal(true);
  };

  const handleDeleteDebt = async (debtId) => {
    if (!confirm('Are you sure you want to delete this debt?')) return;
    
    try {
      await axios.delete(`${API_BASE_PATH}/debts/${debtId}`);
      fetchDebts();
    } catch (error) {
      console.error('Error deleting debt:', error);
    }
  };

  // Calculate summary statistics
  const summary = debts.reduce((acc, debt) => {
  const isLongTerm = debt.type === 'mortgage' || debt.type === 'auto_loan';

  // All debts count toward these totals
  acc.totalOriginal += debt.original_balance || 0;
  acc.allDebtsBalance += debt.current_balance || 0;
  acc.allDebtsMinPayment += debt.minimum_payment || 0;

  // Exclude long-term debt from primary totals
  if (!isLongTerm) {
  acc.totalBalance += debt.current_balance || 0;
  acc.totalMinPayment += debt.minimum_payment || 0;
  } else {
  acc.longTermBalance += debt.current_balance || 0;
  acc.longTermMinPayment += debt.minimum_payment || 0;
  }

  if (debt.type === 'credit_card' && debt.credit_limit) {
  acc.creditUtilization += (debt.current_balance / debt.credit_limit) * 100;
  acc.creditCardCount++;
  }

  return acc;
  }, { 
  totalBalance: 0, 
  totalMinPayment: 0, 
  totalOriginal: 0,
  allDebtsBalance: 0,
  allDebtsMinPayment: 0,
  longTermBalance: 0,
  longTermMinPayment: 0,
  creditUtilization: 0,
  creditCardCount: 0
  });

  const avgCreditUtilization = summary.creditCardCount > 0 
  ? (summary.creditUtilization / summary.creditCardCount).toFixed(1) 
  : 0;

  const totalPaid = summary.totalOriginal - summary.allDebtsBalance;
  const progressPercent = summary.totalOriginal > 0 
  ? ((totalPaid / summary.totalOriginal) * 100).toFixed(1) 
  : 0;

  // Sort function for each debt type group
  const sortDebts = (debts, type) => {
    const config = sortConfig[type];
    if (!config) return debts;

    return [...debts].sort((a, b) => {
      let aVal, bVal;

      switch (config.key) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'balance':
          aVal = a.current_balance;
          bVal = b.current_balance;
          break;
        case 'apr':
          aVal = a.apr;
          bVal = b.apr;
          break;
        case 'min_payment':
          aVal = a.minimum_payment;
          bVal = b.minimum_payment;
          break;
        case 'due_date':
          aVal = a.next_due_date ? new Date(a.next_due_date).getTime() : 0;
          bVal = b.next_due_date ? new Date(b.next_due_date).getTime() : 0;
          break;
        case 'progress':
          if (a.type === 'credit_card' && a.credit_limit) {
            aVal = ((a.credit_limit - a.current_balance) / a.credit_limit) * 100;
          } else if (a.original_balance > 0) {
            aVal = ((a.original_balance - a.current_balance) / a.original_balance) * 100;
          } else {
            aVal = 0;
          }
          
          if (b.type === 'credit_card' && b.credit_limit) {
            bVal = ((b.credit_limit - b.current_balance) / b.credit_limit) * 100;
          } else if (b.original_balance > 0) {
            bVal = ((b.original_balance - b.current_balance) / b.original_balance) * 100;
          } else {
            bVal = 0;
          }
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return config.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return config.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (type, key) => {
    setSortConfig(prev => {
      const currentConfig = prev[type];
      const newDirection = currentConfig?.key === key && currentConfig.direction === 'asc' ? 'desc' : 'asc';
      
      return {
        ...prev,
        [type]: { key, direction: newDirection }
      };
    });
  };

  const SortableHeader = ({ type, sortKey, label }) => {
    const config = sortConfig[type];
    const isActive = config?.key === sortKey;
    
    return (
      <th 
        onClick={() => handleSort(type, sortKey)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {label}
          <i 
            className={`fas fa-sort${isActive ? (config.direction === 'asc' ? '-up' : '-down') : ''}`}
            style={{ 
              fontSize: '11px',
              opacity: isActive ? 1 : 0.3,
              color: isActive ? 'var(--primary-color)' : 'inherit'
            }}
          ></i>
        </div>
      </th>
    );
  };

  // Group and sort debts by type
  const debtsByType = debts.reduce((acc, debt) => {
    if (!acc[debt.type]) acc[debt.type] = [];
    acc[debt.type].push(debt);
    return acc;
  }, {});

  // Sort the types themselves by importance
  const sortedDebtTypes = Object.keys(debtsByType).sort((a, b) => {
    return debtTypeOrder[a] - debtTypeOrder[b];
  });

  const debtTypeLabels = {
    mortgage: 'Mortgage',
    auto_loan: 'Auto Loans',
    personal_loan: 'Personal Loans',
    credit_card: 'Credit Cards',
    student_loan: 'Student Loans',
    other: 'Other'
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: 700, margin: 0 }}>
            <i className="fas fa-credit-card" style={{ marginRight: '10px' }}></i>
            Debts
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
            Track and manage all your debts
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleAddDebt}>
          <i className="fas fa-plus"></i>
          Add Debt
        </button>
      </div>

    {/* Summary Cards */}
    <div style={{ 
    display: 'grid', 
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
    gap: '16px',
    marginBottom: '24px'
    }}>
    <div className="metric-card">
        <div className="metric-label">
        <i className="fas fa-balance-scale"></i>
        Total Debt (Excluding Long-term)
        </div>
        <div className="metric-value" style={{ color: 'var(--danger-color)' }}>
        {formatCurrency(summary.totalBalance)}
        </div>
        {summary.longTermBalance > 0 && (
        <div style={{ 
            fontSize: '11px', 
            color: 'var(--text-secondary)', 
            marginTop: '6px',
            paddingTop: '6px',
            borderTop: '1px solid var(--border-color)'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
            <span>Long-term debt:</span>
            <span style={{ fontWeight: 600 }}>{formatCurrency(summary.longTermBalance)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Grand total:</span>
            <span style={{ fontWeight: 600 }}>{formatCurrency(summary.allDebtsBalance)}</span>
            </div>
        </div>
        )}
    </div>

    <div className="metric-card">
        <div className="metric-label">
        <i className="fas fa-chart-line"></i>
        Overall Progress
        </div>
        <div className="metric-value" style={{ color: 'var(--success-color)' }}>
        {progressPercent}%
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
        {formatCurrency(totalPaid)} paid of {formatCurrency(summary.totalOriginal)}
        </div>
    </div>

    <div className="metric-card">
        <div className="metric-label">
        <i className="fas fa-calendar-alt"></i>
        Min Payment/Month
        </div>
        <div className="metric-value">
        {formatCurrency(summary.totalMinPayment)}
        </div>
        {summary.longTermMinPayment > 0 && (
        <div style={{ 
            fontSize: '11px', 
            color: 'var(--text-secondary)', 
            marginTop: '6px',
            paddingTop: '6px',
            borderTop: '1px solid var(--border-color)'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
            <span>+ Long-term:</span>
            <span style={{ fontWeight: 600 }}>{formatCurrency(summary.longTermMinPayment)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Total:</span>
            <span style={{ fontWeight: 600 }}>{formatCurrency(summary.allDebtsMinPayment)}</span>
            </div>
        </div>
        )}
    </div>

      {summary.creditCardCount > 0 && (
          <div className="metric-card">
          <div className="metric-label">
              <i className="fas fa-percentage"></i>
              Avg Credit Utilization
          </div>
          <div className="metric-value" style={{ 
              color: avgCreditUtilization > 30 ? 'var(--danger-color)' : 'var(--success-color)' 
          }}>
              {avgCreditUtilization}%
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {avgCreditUtilization > 30 ? 'Above recommended' : 'Healthy range'}
          </div>
          </div>
      )}
    </div>

      {/* Debts by Type */}
      {debts.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-credit-card empty-state-icon"></i>
          <h3 className="empty-state-title">No debts tracked</h3>
          <p className="empty-state-description">
            Add your first debt to start tracking your progress
          </p>
          <button className="btn btn-primary" onClick={handleAddDebt} style={{ marginTop: '16px' }}>
            <i className="fas fa-plus"></i>
            Add Your First Debt
          </button>
        </div>
      ) : (
        sortedDebtTypes.map((type) => {
          const typeDebts = sortDebts(debtsByType[type], type);
          
          return (
            <div key={type} style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: 700, 
                marginBottom: '12px',
                color: 'var(--text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {type === 'credit_card' && <i className="fas fa-credit-card" style={{ color: 'var(--primary-color)' }}></i>}
                {debtTypeLabels[type]} ({typeDebts.length})
              </h3>
              
              <div className="table-container" style={{ overflow: 'visible' }}>
                <table className="table" style={{ position: 'relative' }}>
                  <thead>
                    <tr>
                      <SortableHeader type={type} sortKey="name" label="Name" />
                      <SortableHeader type={type} sortKey="balance" label="Balance" />
                      <SortableHeader type={type} sortKey="apr" label="APR" />
                      <SortableHeader type={type} sortKey="min_payment" label="Min Payment" />
                      <SortableHeader type={type} sortKey="due_date" label="Next Due" />
                      <SortableHeader type={type} sortKey="progress" label="Progress" />
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {typeDebts.map(debt => {
                      let paidAmount, paidPercent;
                      
                      if (debt.type === 'credit_card' && debt.credit_limit) {
                        const availableCredit = debt.credit_limit - debt.current_balance;
                        paidPercent = ((availableCredit / debt.credit_limit) * 100).toFixed(1);
                      } else if (debt.original_balance && debt.original_balance > 0) {
                        paidAmount = debt.original_balance - debt.current_balance;
                        paidPercent = ((paidAmount / debt.original_balance) * 100).toFixed(1);
                      } else {
                        paidPercent = 0;
                      }
                      
                      const daysUntilDue = debt.next_due_date 
                        ? Math.ceil((new Date(debt.next_due_date) - new Date()) / (1000 * 60 * 60 * 24))
                        : null;
                      
                      return (
                        <tr
                          key={debt.id}
                          style={{ position: 'relative', zIndex: hoveredDebt === debt.id ? 100 : 'auto' }}
                        >
                          <td>
                            <strong>{debt.name}</strong>
                            {debt.creditor && (
                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                {debt.creditor}
                              </div>
                            )}
                          </td>
                          <td
                            style={{ position: 'relative' }}
                            onMouseEnter={() => debt.type === 'credit_card' && setHoveredDebt(debt.id)}
                            onMouseLeave={() => setHoveredDebt(null)}
                          >
                            <strong style={{ color: 'var(--danger-color)' }}>
                              {formatCurrency(debt.current_balance)}
                            </strong>
                            {debt.type === 'credit_card' && debt.credit_limit && (
                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                of {formatCurrency(debt.credit_limit)} limit
                              </div>
                            )}

                            {/* Hover Tooltip for Credit Cards */}
                            {debt.type === 'credit_card' && hoveredDebt === debt.id && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                marginTop: '8px',
                                padding: '12px',
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: 'var(--border-radius)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                zIndex: 10000,
                                minWidth: '280px',
                                fontSize: '12px',
                                lineHeight: '1.6',
                                pointerEvents: 'none'
                              }}>
                                <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px', color: 'var(--primary-color)' }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                                  Credit Card Details
                                </div>

                                <div style={{ display: 'grid', gap: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--text-secondary)' }}>Current Balance:</span>
                                    <strong>{formatCurrency(debt.current_balance)}</strong>
                                  </div>

                                  {debt.original_balance > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: 'var(--text-secondary)' }}>Original Balance:</span>
                                      <strong>{formatCurrency(debt.original_balance)}</strong>
                                    </div>
                                  )}

                                  {debt.credit_limit && (
                                    <>
                                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>Credit Limit:</span>
                                        <strong>{formatCurrency(debt.credit_limit)}</strong>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>Available Credit:</span>
                                        <strong style={{ color: 'var(--success-color)' }}>
                                          {formatCurrency(debt.credit_limit - debt.current_balance)}
                                        </strong>
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>Utilization:</span>
                                        <strong style={{
                                          color: ((debt.current_balance / debt.credit_limit) * 100) > 30
                                            ? 'var(--danger-color)'
                                            : 'var(--success-color)'
                                        }}>
                                          {((debt.current_balance / debt.credit_limit) * 100).toFixed(1)}%
                                        </strong>
                                      </div>
                                    </>
                                  )}

                                  {debt.statement_closing_day && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid var(--border-color)' }}>
                                      <span style={{ color: 'var(--text-secondary)' }}>Statement Closes:</span>
                                      <strong>Day {debt.statement_closing_day}</strong>
                                    </div>
                                  )}

                                  {debt.interest_calculation_method && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: 'var(--text-secondary)' }}>Interest Method:</span>
                                      <strong>{debt.interest_calculation_method}</strong>
                                    </div>
                                  )}

                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--text-secondary)' }}>AutoPay:</span>
                                    <strong style={{ color: debt.is_autopay ? 'var(--success-color)' : 'var(--text-secondary)' }}>
                                      {debt.is_autopay ? (
                                        <><i className="fas fa-check-circle" style={{ marginRight: '4px' }}></i>Enabled</>
                                      ) : (
                                        <><i className="fas fa-times-circle" style={{ marginRight: '4px' }}></i>Disabled</>
                                      )}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                          <td>{debt.apr}%</td>
                          <td>{formatCurrency(debt.minimum_payment)}</td>
                          <td>
                            {debt.next_due_date ? (
                              <>
                                {formatLocalDate(debt.next_due_date)}
                                {daysUntilDue !== null && (
                                  <div style={{ 
                                    fontSize: '11px', 
                                    color: daysUntilDue < 7 ? 'var(--danger-color)' : 'var(--text-secondary)' 
                                  }}>
                                    {daysUntilDue < 0 ? 'Overdue!' : `in ${daysUntilDue} days`}
                                  </div>
                                )}
                              </>
                            ) : '-'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ 
                                flex: 1,
                                height: '6px',
                                background: 'var(--bg-tertiary)',
                                borderRadius: '3px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${Math.min(100, Math.max(0, paidPercent))}%`,
                                  height: '100%',
                                  background: debt.type === 'credit_card' 
                                    ? (paidPercent > 70 ? 'var(--success-color)' : paidPercent > 30 ? 'var(--warning-color)' : 'var(--danger-color)')
                                    : 'var(--success-color)',
                                  transition: 'width 0.3s ease'
                                }}></div>
                              </div>
                              <span style={{ fontSize: '12px', fontWeight: 600, minWidth: '45px' }}>
                                {paidPercent}%
                              </span>
                            </div>
                            {debt.type === 'credit_card' && (
                              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                Available credit
                              </div>
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className="btn-table-action"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditDebt(debt);
                                }}
                                title="Edit debt"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn-table-action danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDebt(debt.id);
                                }}
                                title="Delete debt"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <AddDebtModal
          debt={editingDebt}
          onClose={() => setShowAddModal(false)}
          onSave={() => {
            setShowAddModal(false);
            fetchDebts();
          }}
        />
      )}
    </div>
  );
};

export default Debts;